import React from 'react';
import { motion } from 'framer-motion';
import {
    RiMailLine,
    RiPhoneLine,
    RiMapPinLine
} from 'react-icons/ri';
import Logo from '../Assets/image.png';

const Footer = () => {
    const quickLinks = [
        { name: 'Home', href: '/' },
        { name: 'Products', href: '/products' },
        { name: 'About Us', href: '/about' },
        { name: 'Contact Us', href: '/contact' },
    ];

    const products = [
        { name: 'Stretch Film', href: '/products/stretch-film' },
        { name: 'Liner Bags', href: '/products/liner-bags' },
        { name: 'Jumbo Bags (FIBC)', href: '/products/jumbo-bags' },
        { name: 'Bubble Wrap', href: '/products/bubble-wrap' },
        { name: 'HDPE Bags', href: '/products/hdpe-bags' },
        { name: 'PP Strip Roll', href: '/products/pp-strip-roll' },
    ];

    const contactInfo = [
        {
            icon: RiMailLine,
            text: 'info@polyzent.com',
            href: 'mailto:info@polyzent.com'
        },
        {
            icon: RiPhoneLine,
            text: '+1 (434) 241-7488',
            href: 'tel:+14342417488'
        },
        {
            icon: RiMapPinLine,
            text: '1000 Robins Road, Building 1, Lynchburg, VA 24502',
            href: 'https://www.google.com/maps/search/?api=1&query=1000+Robins+Road,+Building+1,+Lynchburg,+VA+24502'
        }
    ];

    return (
        <footer className="bg-slate-900 text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-400/20"></div>
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.1),transparent_50%)]"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
                    
                    {/* Company Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-2"
                    >
                        <div className="mb-6">
                            <img
                                src={Logo}
                                alt="Polyzent Trading Inc Logo"
                                className="h-28 w-auto"
                            />
                        </div>
                        
                        <p className="text-gray-300 mb-6 leading-relaxed text-lg max-w-md">
                            Delivering excellence through innovation. We create premium solutions that drive success for businesses worldwide. Manufacturing in Lynchburg, Virginia. Made in USA.
                        </p>

                        {/* Contact Info */}
                        <div className="space-y-3 mb-6">
                            {contactInfo.map((contact, index) => (
                                <motion.a
                                    key={index}
                                    href={contact.href}
                                    className="flex items-center text-gray-300 hover:text-amber-400 transition-colors duration-300 group"
                                    whileHover={{ x: 5 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <contact.icon className="w-5 h-5 mr-3 text-amber-400 group-hover:text-amber-300" />
                                    <span className="text-sm">{contact.text}</span>
                                </motion.a>
                            ))}
                        </div>

                        
                    </motion.div>

                    {/* Quick Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <h4 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                            Quick Links
                        </h4>
                        <ul className="space-y-3">
                            {quickLinks.map((link, index) => (
                                <motion.li
                                    key={link.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 + 0.2 }}
                                >
                                    <a
                                        href={link.href}
                                        className="text-gray-300 hover:text-amber-400 transition-colors duration-300 flex items-center group"
                                    >
                                        <span className="w-0 group-hover:w-2 h-0.5 bg-amber-400 transition-all duration-300 mr-0 group-hover:mr-3 rounded-full"></span>
                                        {link.name}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Services/Products */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <h4 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                            Our Products
                        </h4>
                        <ul className="space-y-3">
                            {products.map((product, index) => (
                                <motion.li
                                    key={product.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 + 0.3 }}
                                >
                                    <a 
                                        href={product.href} 
                                        className="text-gray-400 text-sm flex items-center hover:text-gray-300 transition-colors duration-300"
                                    >
                                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-3 opacity-70"></div>
                                        {product.name}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                {/* Bottom Bar */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="border-t border-slate-700/50 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
                >
                    <p className="text-gray-500 text-sm mb-4 md:mb-0">
                        © {new Date().getFullYear()} Polyzent Trading Inc. All rights reserved.
                    </p>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer;
